/**
 * The live game: owns the render stage (GDD fidelity), the fixed-60 Hz simulation and the loop (GDD §11).
 * Each step flies the jet, then runs the game simulation (rules and enemies, GDD §4–5) and its explosions; each
 * render interpolates the jet, camera, enemies, shots and tracers between the last two steps and poses the stage
 * objects directly through preallocated state, so a frame allocates nothing (GDD §10.5). The attract-mode autopilot
 * flies unless a player pilots; the game flow (src/flow/gameFlow.ts) hands the jet to the player's controls and
 * listens through the hooks.
 */
import { Vector2 } from 'three';
import { lerp } from '../core/math.ts';
import { createLoop, type Loop } from '../core/loop.ts';
import { mulberry32, type Rng } from '../core/rng.ts';
import { createChaseRig } from '../camera/chaseRig.ts';
import { cameraProfile, profileFor, type ProfileName } from '../camera/profile.ts';
import { createRigPose, type CameraRig } from '../camera/rigs.ts';
import { createTopCameraRig, type TopCameraRig } from '../camera/topRig.ts';
import { flameScale, glowScale, strobeOn } from '../effects/jetFx.ts';
import type { ParticleSystems } from '../effects/particles.ts';
import { createSpray, type Spray } from '../effects/spray.ts';
import { createStage, type Stage } from '../render/stage.ts';
import { createExplosions, type Explosions } from '../effects/explosions.ts';
import { addShake } from '../camera/topRig.ts';
import { autopilot } from '../sim/autopilot.ts';
import type { GameSim } from '../sim/context.ts';
import type { Fx, SoundTap } from '../sim/fx.ts';
import { createGameSim, productionStreams, shiftGameSim, stepGameSim } from '../sim/gameSim.ts';
import type { CrashCause, EnemyType, SimEvent } from '../sim/state.ts';
import { autoFireTarget } from '../rules/autoFire.ts';
import { quitRun, restartRun, takeOver } from '../rules/lives.ts';
import { ENEMY_TYPES } from '../sim/state.ts';
import { loadSave, saveHigh } from '../storage/storage.ts';
import { newRunSeed } from '../core/rng.ts';
import { createFlight, SHIFT_AT, stepFlight, type FlightState } from '../sim/flight.ts';
import { NEUTRAL, type PilotInput } from '../sim/pilot.ts';
import { worldSettings } from '../world/fidelity.ts';
import { MODES, type Fidelity, type ModeName } from '../world/modes.ts';
import { WORLDS, type WorldProfile } from '../world/profiles.ts';
import { poseTextures } from '../world/textureMotion.ts';
import { WORLD_Z0, worldZ1 } from '../world/water.ts';

export const STEP_HZ = 60;
const DT = 1 / STEP_HZ;

/** A snapshot of the run for tests and the HUD (allocates: never called by the loop). */
export function runInfo(g: GameSim): RunInfo {
  const r = g.run;
  const enemies = { ship: 0, heli: 0, fuel: 0, jet: 0 };
  for (let i = 0; i < g.ents.count; i++) {
    const e = g.ents.items[i];
    if (e) enemies[e.type]++;
  }
  const peaks = { ship: 0, heli: 0, fuel: 0, jet: 0 };
  let overflows = 0;
  for (const t of ENEMY_TYPES) {
    peaks[t] = g.pool.types[t].peak;
    overflows += g.pool.types[t].overflows;
  }
  return {
    level: r.level,
    lives: r.lives,
    score: r.score,
    hi: r.hi,
    fuel: r.fuel,
    alive: r.alive,
    invulnerable: r.invuln > 0,
    title: r.title,
    player: r.player,
    runs: r.runs,
    crashes: r.crashes,
    lastCause: r.lastCause,
    enemies,
    shots: g.shots.count,
    tracers: g.tracers.filter((t) => t.life > 0).length,
    bridge: { intact: g.bridge.intact, hp: g.bridge.hp, z: g.bridge.z },
    checkpointZ: r.checkpointZ,
    section: r.sectionIndex,
    events: g.eventCount,
    peaks,
    overflows,
  };
}

export interface GameOptions {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /** Seed of the effects stream: spray, sky-life respawns (master plan porting rule 3). */
  seed?: number;
  /** Floating-origin threshold; −Infinity renders the unshifted twin of a run (tests). */
  shiftAt?: number;
  /** Production is 'gdd'; 'concept' is for comparisons with the concept renderer. */
  fidelity?: Fidelity;
  /**
   * 'classic' (v1): the valley, the top-down camera and the landscape or portrait layout by screen shape.
   * 'canyon' (v1.1 hook, dev only): the canyon world, sunset look and chase camera.
   */
  mode?: 'classic' | 'canyon';
  /** Run seeds for the game simulation: tests pass a number (each run takes the next); production draws them. */
  runSeed?: number;
  /** Level of the first run (tests and the dev flag ?level=n). */
  startLevel?: number;
  /** A player flies (dev keyboard until M5); otherwise the attract-mode autopilot. */
  player?: boolean;
  /**
   * Rules and enemies (default on in classic mode). Off flies the M2 world only, e.g. for the no-pop test, whose
   * unshifted twin run can't hold a game: the bridge and sections live on the 800 m floating origin.
   */
  game?: boolean;
}

/** The run as the HUD (M5) and the tests read it. */
export interface RunInfo {
  level: number;
  lives: number;
  score: number;
  hi: number;
  fuel: number;
  alive: boolean;
  invulnerable: boolean;
  title: number;
  player: boolean;
  runs: number;
  crashes: number;
  lastCause: CrashCause | null;
  enemies: Record<EnemyType, number>;
  shots: number;
  tracers: number;
  bridge: { intact: boolean; hp: number; z: number };
  checkpointZ: number;
  section: number;
  events: number;
  /** Peak enemy slots used and spawns refused, per type (pools must never overflow). */
  peaks: Record<EnemyType, number>;
  overflows: number;
}

export interface GameState {
  mode: 'classic' | 'canyon';
  rig: CameraRig['name'];
  timeScale: number;
  clock: number;
  shifts: number;
  profile: ProfileName;
  jet: { x: number; y: number; z: number; speed: number; roll: number; vx: number };
  /** Terrain height under the jet (the bank rule: above 1.4 m is a crash, GDD §4.2). */
  ground: number;
  /** River centerline and half-width at the jet. */
  river: { center: number; width: number };
  camera: { x: number; y: number; z: number; fov: number };
  sprayCount: number;
  /** The game simulation (classic mode). */
  run: RunInfo | null;
}

export interface Game {
  readonly stage: Stage;
  readonly flight: FlightState;
  readonly loop: Loop;
  readonly profile: ProfileName;
  /** Simulated seconds. */
  readonly clock: number;
  /** World shifts so far. */
  readonly shifts: number;
  /** Simulation speed (1 normal; below 1 slow motion, for the v1.1 bridge camera). */
  timeScale: number;
  readonly rig: CameraRig;
  /** The rules-and-enemies simulation (classic mode), for the HUD (M5) and test hooks. */
  readonly sim: GameSim | null;
  readonly explosions: Explosions | null;
  /** A held input instead of the autopilot (tests, the dev keyboard); null gives control back to the autopilot. */
  pilot: PilotInput | null;
  /** With a held input: steer the autopilot's line down the river, keeping the held throttle and fire (tests). */
  cruise: boolean;
  /** The player's steering sensitivity, 0.5–1.5 (GDD §4.1); the autopilot always flies at 1. */
  sensitivity: number;
  /** Touch auto-fire for the player (Q3): fire while an enemy or intact bridge is in the line of fire. */
  autoFire: boolean;
  /** The game flow's listeners. */
  hooks: GameHooks;
  /** True while paused (GDD §7.1): no steps, no drawing. */
  readonly paused: boolean;
  /** Freezes the simulation and stops drawing after the current frame. */
  pause(): void;
  /** Resumes from a pause without simulating the time spent paused. */
  resume(): void;
  /** The player takes over the title demo (judgment call 28). */
  takeOver(): void;
  /** A new player run from the checkpoint (pause menu, Restart). */
  restart(): void;
  /** Back to the title demo (pause menu, Quit). */
  quit(): void;
  /** One fixed simulation step. */
  step(): void;
  /** Draws the scene `alpha` of the way from the previous step to the current one. */
  render(alpha: number): void;
  /** Runs whole fixed steps for `seconds`, then draws the current step (tests, benches). */
  advance(seconds: number): void;
  resize(width: number, height: number): void;
  start(): void;
  stop(): void;
  state(): GameState;
  dispose(): void;
}

/** Where the game flow listens. Every hook runs inside a frame, so none may allocate (GDD §10.5). */
export interface GameHooks {
  /** Start of each fixed step, before the pilot input is read (input polling and merging). */
  input?(): void;
  /** Each simulation event, in order (banners, flow, haptics). */
  event?(e: SimEvent): void;
  /** End of each fixed step. */
  step?(dt: number): void;
  /** After each drawn frame (the HUD). */
  render?(): void;
  /** Each explosion, with its size (haptics). */
  boom?(size: number): void;
  /** The simulation's effects calls that make a sound (the audio, GDD §9). */
  sound?: SoundTap;
  /** The world moved by `dz` (the 800 m floating origin). */
  shift?(dz: number): void;
}

/** Camera and world mode for a screen shape: the GDD §3.2 profiles map to the concept's classic and phone modes. */
const modeFor = (profile: ProfileName): ModeName => (profile === 'portrait' ? 'phone' : 'classic');

export async function createGame(opts: GameOptions): Promise<Game> {
  const fidelity = opts.fidelity ?? 'gdd';
  const gameMode = opts.mode ?? 'classic';
  let profile = profileFor(opts.width, opts.height);
  const stageMode: ModeName = gameMode === 'canyon' ? 'canyon' : modeFor(profile);
  const stage = await createStage({
    canvas: opts.canvas,
    mode: stageMode,
    width: opts.width,
    height: opts.height,
    fidelity,
  });
  const W: WorldProfile = WORLDS[MODES[stageMode].world];
  const shiftAt = opts.shiftAt ?? SHIFT_AT;
  const fx: Rng = mulberry32(opts.seed ?? 1);
  // Camera shake jitter draws at display rate; its own stream keeps the simulation independent of the frame rate.
  const shakeRand: Rng = mulberry32(((opts.seed ?? 1) ^ 0x9e3779b9) >>> 0);

  const flight = createFlight(W);
  const topRig: TopCameraRig | null =
    gameMode === 'classic' ? createTopCameraRig(flight, W, cameraProfile(profile), shakeRand) : null;
  const rig: CameraRig = topRig ?? createChaseRig(flight);
  const input: PilotInput = { ...NEUTRAL };
  const spray: Spray = createSpray(stage.particles.spray, fx, W === WORLDS.canyon);
  const systems = Object.values(stage.particles) as ParticleSystems[keyof ParticleSystems][];

  // Rules and enemies (classic mode; the v1.1 canyon flag only flies). Explosions shake the top camera.
  const hooks: GameHooks = {};
  const explosions: Explosions | null =
    topRig && (opts.game ?? true)
      ? createExplosions(stage.particles, fx, (size) => {
          addShake(topRig.state, size);
          hooks.boom?.(size);
        })
      : null;
  // The simulation's effects: the explosions draw them and the audio hears them.
  const simFx: Fx | null = explosions
    ? {
        boom(x, y, z, s) {
          explosions.boom(x, y, z, s);
          hooks.sound?.boom(x, y, z, s);
        },
        splash(x, z, s) {
          explosions.splash(x, z, s);
          hooks.sound?.splash(x, z, s);
        },
        spanSplash(x, z) {
          explosions.spanSplash(x, z);
          hooks.sound?.spanSplash(x, z);
        },
        flak(x, y, z) {
          explosions.flak(x, y, z);
          hooks.sound?.flak(x, y, z);
        },
        muzzle(x, y, z, vx, vz, cause) {
          explosions.muzzle(x, y, z, vx, vz, cause);
          hooks.sound?.muzzle(x, y, z, vx, vz, cause);
        },
        bridgeHit(x, y, z) {
          explosions.bridgeHit(x, y, z);
          hooks.sound?.bridgeHit(x, y, z);
        },
        sinkSmoke: (x, z, dt) => explosions.sinkSmoke(x, z, dt),
        bridgeSmoke: (x, y, z, dt) => explosions.bridgeSmoke(x, y, z, dt),
        step: (dt) => explosions.step(dt),
      }
    : null;
  const seeds = { next: opts.runSeed ?? 0 };
  const game: GameSim | null =
    topRig && simFx
      ? createGameSim({
          W,
          ground: stage.world.surface,
          jet: flight,
          rules: 'gdd',
          fx: simFx,
          streams: productionStreams(fx),
          startLevel: opts.startLevel,
          player: opts.player ?? false,
          hi: loadSave().hi,
          nextRunSeed: opts.runSeed === undefined ? newRunSeed : () => seeds.next++,
        })
      : null;
  const events = { read: game ? game.eventCount : 0 };
  // `live`: the real-time loop should run (start/stop); a pause stops drawing without changing it.
  const control = {
    pilot: null as PilotInput | null,
    cruise: false,
    sensitivity: 1,
    autoFire: false,
    paused: false,
    live: false,
  };
  // Simulation state in one object: V8 updates number fields in place (no per-step allocation).
  const sim = { clock: 0, shifts: 0, timeScale: 1 };

  // The previous step, for interpolation (shifted with the world, so interpolation never spans a shift).
  const prev = { x: flight.x, y: flight.y, z: flight.z, roll: 0, yaw: 0, pitch: 0, speed: flight.speed, clock: 0 };
  // Interpolated flight handed to the camera rig.
  const lerpFlight: FlightState = { ...flight };
  const pose = createRigPose();

  const { camera, lights, sky, views, textures, world, renderer, atmosphere, speedLines } = stage;
  const jet = views.jet;
  const jetParts = jet.userData;
  const drawingBuffer = new Vector2();
  let width = opts.width;
  let height = opts.height;

  /** Camera lens and the particles' point scale (concept L844); called on resize and when the rig's lens changes. */
  function applyLens(fov: number): void {
    camera.fov = fov;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.getDrawingBufferSize(drawingBuffer);
    for (const p of systems) p.setScale(drawingBuffer.y, fov);
  }
  applyLens(topRig ? topRig.profile.fov : MODES[stageMode].fov);

  /** Reacts to the step's events: a checkpoint restart is a camera cut (judgment call 22); a player's game over saves. */
  function readEvents(z0: number): void {
    if (!game) return;
    for (; events.read < game.eventCount; events.read++) {
      const e = game.events[events.read % game.events.length];
      if (!e) continue;
      hooks.event?.(e);
      if (e.type === 'respawn') {
        const jump = flight.z - z0;
        if (jump !== 0) {
          // The sky layers move with the jet so the view around the restart looks as it did.
          atmosphere?.shiftZ(jump);
          rig.cut?.(flight);
        }
        prev.x = flight.x;
        prev.y = flight.y;
        prev.z = flight.z;
        prev.roll = flight.roll;
        prev.yaw = flight.yaw;
        prev.pitch = flight.pitch;
      } else if (e.type === 'gameOver' && game.run.player) saveHigh(game.run.hi);
    }
  }

  function step(): void {
    // A pause raised during this frame (a gamepad's Start) stops the steps still due.
    if (control.paused) return;
    hooks.input?.();
    if (control.paused) return;
    const dt = DT * sim.timeScale;
    prev.x = flight.x;
    prev.y = flight.y;
    prev.z = flight.z;
    prev.roll = flight.roll;
    prev.yaw = flight.yaw;
    prev.pitch = flight.pitch;
    prev.speed = flight.speed;
    prev.clock = sim.clock;
    sim.clock += dt;

    const sensitivity = control.pilot ? control.sensitivity : 1;
    if (control.pilot) {
      if (control.cruise) autopilot(flight, W, 1, input, null);
      else input.steer = control.pilot.steer;
      input.throttle = control.pilot.throttle;
      input.fire = control.pilot.fire || (control.autoFire && game !== null && autoFireTarget(game));
    } else autopilot(flight, W, 1, input, game);
    const alive = game ? game.run.alive : true;
    const shift = stepFlight(flight, input, dt, sensitivity, alive, shiftAt);
    if (shift) {
      prev.z += shift;
      sim.shifts++;
      for (const p of systems) p.shiftZ(shift);
      atmosphere?.shiftZ(shift);
      rig.shiftZ(shift);
      if (game) shiftGameSim(game, shift);
      explosions?.shiftZ(shift);
      hooks.shift?.(shift);
    }
    if (game) {
      const z0 = flight.z;
      stepGameSim(game, dt, sim.clock, input.fire, shift);
      readEvents(z0);
    }
    spray.step(flight, dt, game ? game.run.alive : true);
    rig.step(flight, dt);
    atmosphere?.step(dt, sim.clock, flight.z, fx);
    speedLines?.update(dt, flight.speed, fx);
    for (const p of systems) p.update(dt);
    hooks.step?.(dt);
  }

  /** Runs a change to the run outside a step, then reacts to its events (camera cut, banners). */
  function runChange(change: (g: GameSim) => void): void {
    if (!game) return;
    const z0 = flight.z;
    change(game);
    readEvents(z0);
  }

  function render(alpha: number): void {
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const t = lerp(prev.clock, sim.clock, a);
    lerpFlight.x = lerp(prev.x, flight.x, a);
    lerpFlight.y = lerp(prev.y, flight.y, a);
    lerpFlight.z = lerp(prev.z, flight.z, a);
    lerpFlight.roll = lerp(prev.roll, flight.roll, a);
    lerpFlight.yaw = lerp(prev.yaw, flight.yaw, a);
    lerpFlight.pitch = lerp(prev.pitch, flight.pitch, a);
    lerpFlight.speed = lerp(prev.speed, flight.speed, a);

    rig.pose(lerpFlight, a, t, pose);
    if (pose.fov !== camera.fov) applyLens(pose.fov);
    camera.position.set(pose.pos[0], pose.pos[1], pose.pos[2]);
    camera.lookAt(pose.target[0], pose.target[1], pose.target[2]);
    if (pose.roll) camera.rotateZ(pose.roll);
    sky.position.copy(camera.position);
    lights.aim(pose.sun[0], pose.sun[1]);

    jet.position.set(lerpFlight.x, lerpFlight.y, lerpFlight.z);
    jet.rotation.set(lerpFlight.pitch, lerpFlight.yaw, lerpFlight.roll, 'YXZ');
    for (let i = 0; i < jetParts.flames.length; i++) jetParts.flames[i]?.scale.set(1, 1, flameScale(t, i));
    jetParts.glow.scale.setScalar(glowScale(t));
    jetParts.strobe.visible = strobeOn(t);
    if (game && explosions) views.poseSim(game, a, explosions.light);

    poseTextures(textures, world.islandFoamMap, t, stage.settings.waterScroll, sim.shifts);
    renderer.render(stage.scene, camera);
    hooks.render?.();
  }

  const loop = createLoop({ step, render, hz: STEP_HZ });
  let raf = 0;
  const tick = (now: number): void => {
    raf = requestAnimationFrame(tick);
    loop.frame(now);
    // Paused: this frame drew the paused scene; nothing more until resume (GDD §7.1).
    if (control.paused) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };

  const api: Game = {
    stage,
    flight,
    loop,
    sim: game,
    explosions,
    get pilot() {
      return control.pilot;
    },
    set pilot(p: PilotInput | null) {
      control.pilot = p;
    },
    get cruise() {
      return control.cruise;
    },
    set cruise(on: boolean) {
      control.cruise = on;
    },
    get sensitivity() {
      return control.sensitivity;
    },
    set sensitivity(v: number) {
      control.sensitivity = v;
    },
    get autoFire() {
      return control.autoFire;
    },
    set autoFire(on: boolean) {
      control.autoFire = on;
    },
    hooks,
    get paused() {
      return control.paused;
    },
    pause() {
      control.paused = true;
      loop.pause();
    },
    resume() {
      if (!control.paused) return;
      control.paused = false;
      loop.resume();
      if (control.live && !raf) raf = requestAnimationFrame(tick);
    },
    takeOver() {
      runChange(takeOver);
    },
    restart() {
      runChange(restartRun);
      render(1);
    },
    quit() {
      runChange(quitRun);
      render(1);
    },
    get profile() {
      return profile;
    },
    get clock() {
      return sim.clock;
    },
    get shifts() {
      return sim.shifts;
    },
    get timeScale() {
      return sim.timeScale;
    },
    set timeScale(v: number) {
      sim.timeScale = Math.max(0, v);
    },
    rig,
    step,
    render,
    advance(seconds) {
      const n = Math.round(seconds * STEP_HZ);
      for (let i = 0; i < n; i++) step();
      render(1);
    },
    resize(w, h) {
      width = w;
      height = h;
      renderer.setSize(w, h, false);
      const next = profileFor(w, h);
      if (topRig && next !== profile) {
        profile = next;
        topRig.profile = cameraProfile(profile);
        // Shadow texel density follows the layout (D4): rebuild the shadow map at the new profile's size.
        const m = MODES[modeFor(profile)];
        const s = worldSettings(m, fidelity, WORLD_Z0, worldZ1(m));
        const shadow = lights.sun.shadow;
        if (shadow.mapSize.x !== s.shadowMapSize) {
          shadow.mapSize.set(s.shadowMapSize, s.shadowMapSize);
          shadow.map?.dispose();
          shadow.map = null;
        }
      }
      applyLens(topRig ? topRig.profile.fov : camera.fov);
    },
    start() {
      control.live = true;
      if (!raf) raf = requestAnimationFrame(tick);
    },
    stop() {
      control.live = false;
      cancelAnimationFrame(raf);
      raf = 0;
    },
    state() {
      return {
        mode: gameMode,
        rig: rig.name,
        timeScale: sim.timeScale,
        clock: sim.clock,
        shifts: sim.shifts,
        profile,
        jet: { x: flight.x, y: flight.y, z: flight.z, speed: flight.speed, roll: flight.roll, vx: flight.vx },
        ground: world.surface(flight.x, flight.z),
        river: { center: W.center(flight.z), width: W.width(flight.z) },
        camera: { x: camera.position.x, y: camera.position.y, z: camera.position.z, fov: camera.fov },
        sprayCount: stage.particles.spray.count,
        run: game ? runInfo(game) : null,
      };
    },
    dispose() {
      api.stop();
      stage.dispose();
    },
  };
  return api;
}
